import { nectarApi } from '@/features/nectar/api/client'
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import axios from 'axios'
import { useRef } from 'react'
import type { MethodId } from './payoutMethods'

export type RequestPayoutVars = {
  amountUsd: string
  method: MethodId
  methodInput: Record<string, string>
  /**
   * Caller-supplied Idempotency-Key. When provided, the ref-minted key machinery is bypassed
   * entirely — used by the PayPal callback page where the key lives in the ceremony stash so a
   * refresh/StrictMode double-fire replays instead of double-debiting (IV2).
   */
  idempotencyKey?: string
}

export const useRequestPayout = (): UseMutationResult<void, unknown, RequestPayoutVars> => {
  const queryClient = useQueryClient()
  // IV4: one Idempotency-Key per confirmed-submission body. Re-mint when the body changes
  // (a retry with edited amount/method/input is a genuinely new request); reuse the key for a
  // re-fire of the same body (double-click / re-render) so the backend dedups instead of
  // double-debiting. Lives in a ref so it never regenerates on render.
  const idempotency = useRef<{ signature: string; key: string } | null>(null)

  return useMutation<void, unknown, RequestPayoutVars>({
    mutationFn: ({ method, amountUsd, methodInput, idempotencyKey }) => {
      let key: string
      if (idempotencyKey) {
        // Caller owns the key lifecycle (PayPal ceremony stash) — skip the ref machinery.
        key = idempotencyKey
      } else {
        // Canonical body signature: methodInput keys are sorted so a re-fire of the same logical
        // body is order-stable (BS3's multi-key methodInput must not mint a fresh key — that would
        // be an unseen key + same body = a real second debit).
        const signature = JSON.stringify({
          method,
          amountUsd,
          methodInput: Object.fromEntries(Object.entries(methodInput).sort(([a], [b]) => a.localeCompare(b)))
        })
        if (idempotency.current?.signature !== signature) {
          idempotency.current = { signature, key: crypto.randomUUID() }
        }
        key = idempotency.current.key
      }
      return nectarApi
        .post<void>('/v1/user/payouts', { method, amountUsd, methodInput }, { headers: { 'Idempotency-Key': key } })
        .catch((err) => {
          // IV3: a 409 is an idempotency collision — the payout was already accepted under this
          // key. Resolve as a silent duplicate-success so onSuccess invalidates and the modal
          // closes with the normal toast; the user cannot tell it was a replay. All other
          // non-2xx errors stay errors.
          if (axios.isAxiosError(err) && err.response?.status === 409) {
            return
          }
          throw err
        })
    },
    onSuccess: async () => {
      // IV4: clear the key once the payout is accepted (a real 2xx or a 409 replay). The modal is
      // always mounted (BalanceWidget renders it with an `open` prop), so without this the ref
      // would survive a close/re-open and a second identical withdrawal would reuse the consumed
      // key → silently deduped to nothing. A key retained after a *failed* attempt is safe to
      // reuse (the backend consumed nothing) and dedups an honest retry.
      idempotency.current = null
      await queryClient.invalidateQueries({ queryKey: ['balance'] })
      await queryClient.invalidateQueries({ queryKey: ['ledger'] })
    }
  })
}
